# WeeklySchedule.vue
<template>
  <Card title="Weekly Schedule" :title-level="1" margin="mb-2" body-class="p-0">
    <template #actions>
      <label class="form-check form-switch">
        <input class="form-check-input" type="checkbox" v-model="showAllShifts">
        <span class="form-check-label">{{ __DICT__.schedule.showAllShifts }}</span>
      </label>
    </template>
      <div class="accordion" id="scheduleAccordion">
        <template v-for="(day, dayName) in weeklySchedule" :key="dayName">
          <AccordionItem
              v-if="shouldShowDay(day)"
              :id="'collapse-' + sanitizeId(dayName)"
              :title="dayName"
              :expanded="isToday(dayName)"
              parent-id="scheduleAccordion"
              body-class="p-0"
          >
            <div class="table-responsive">
              <table class="table table-vcenter m-0">
                <thead>
                <tr class="schedule-header-row">
                  <th>{{ __DICT__.schedule.period }}</th>
                  <th>{{ __DICT__.time }}</th>
                  <th>{{ __DICT__.role }}</th>
                  <th>{{ __DICT__.location }}</th>
                  <th v-if="showAllShifts">{{ __DICT__.reps }}</th>
                </tr>
                </thead>
                <tbody>
                <template v-for="(slot, period) in day" :key="`${dayName}-${period}`">
                  <template v-if="showAllShifts">
                    <tr v-for="shift in getUniqueShiftsForPeriod(dayName, period)" :key="shift.id"
                        class="cursor-pointer schedule-row-hover"
                        @click="handleShiftClick(shift)">
                      <td class="text-muted text-capitalize">
                        {{ period }}
                      </td>
                      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                      <td>
                        {{ shift.Start }} - {{ shift.End }}
                      </td>
                      <td>
                          <span
                              v-if="shift.Role"
                              class="badge text-white"
                              :class="getRoleBadgeClass(shift.Role)"
                          >
                            {{ shift.Role }}
                          </span>
                        <span v-else class="text-muted">{{ __DICT__.schedule.noRoleAssigned }}</span>
                      </td>
                      <td>
                        <a
                            v-if="shift.Location && shift['URL or Lat/Long']"
                            :href="getLocationUrl(shift['URL or Lat/Long'])"
                            target="_blank"
                            class="text-decoration-none"
                            @click.stop
                        >
                          {{ shift.Location }}
                        </a>
                        <span v-else>{{ shift.Location || 'No Location' }}</span>
                      </td>
                      <td>
                        <div class="d-flex flex-wrap gap-1">
                          {{ shift.reps.length }} {{ __DICT__.reps }}
                        </div>
                      </td>
                    </tr>
                  </template>
                  <tr v-else-if="shouldShowShift(slot)"
                      class="cursor-pointer schedule-row-hover"
                      @click="handleShiftClick(slot)">
                    <td class="text-muted text-capitalize">
                      {{ period }}
                    </td>
                    <td>
                      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                      <template v-if="hasShift(slot)">
                        {{ slot.Start }} - {{ slot.End }}
                      </template>
                      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                      <span v-else class="text-muted">--:-- - --:--</span>
                    </td>
                    <td>
                        <span
                            v-if="slot.Role"
                            class="badge text-white"
                            :class="getRoleBadgeClass(slot.Role)"
                        >
                          {{ slot.Role }}
                        </span>
                      <span v-else class="text-muted">{{ __DICT__.schedule.noRoleAssigned }}</span>
                    </td>
                    <td>
                      <a
                          v-if="slot.Location && slot['URL or Lat/Long']"
                          :href="getLocationUrl(slot['URL or Lat/Long'])"
                          target="_blank"
                          class="text-decoration-none"
                          @click.stop
                      >
                        {{ slot.Location }}
                      </a>
                      <span v-else>{{ slot.Location || 'No Location' }}</span>
                    </td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>
          </AccordionItem>
        </template>
      </div>
  </Card>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { AccordionItem, Card } from "@/components/atomic";
import { encodeBase64Url } from "@/services/base64.js";

const props = defineProps({
  repData: {
    type: Object,
    required: true
  },
  allRepsData: {
    type: Array,
    required: true
  },
  brand: {
    type: String,
    required: true
  }
});

const router = useRouter();
const showAllShifts = ref(false);

// Sanitize day names to create valid CSS selector IDs
const sanitizeId = (str) => {
  return str
    .toLowerCase()
    .replace(/\s+/g, '-')      // Replace spaces with hyphens
    .replace(/[()]/g, '')       // Remove parentheses
    .replace(/[^a-z0-9-]/g, '') // Remove any other invalid characters
};

// Generate consistent shift IDs using URL-safe base64
const generateShiftId = (shift, dayName, period) => {
  return encodeBase64Url(`${shift.Role || ''}~${shift.Location || ''}~${shift.Start || ''}~${shift.End || ''}~${dayName}~${period}`);
};

const getColorFromString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['blue', 'green', 'red', 'yellow', 'purple', 'azure', 'orange', 'teal', 'cyan', 'indigo'];
  return colors[Math.abs(hash) % colors.length];
};

const getRoleBadgeClass = (role) => {
  return `bg-${getColorFromString(role)}`;
};

const isToday = (dayName) => {
  const today = new Date().getDay();
  // Note: Adjust the mapping based on your exact day names
  const dayMap = {
    'Sunday': 0,
    'Monday': 1,
    'Tuesday': 2,
    'Wednesday': 3,
    'Thursday': 4,
    'Friday': 5,
    'Saturday': 6
  };

  // Handle special cases for arrival/departure days
  if (dayName.includes('Friday')) {
    return today === 5;  // Friday
  }
  if (dayName.includes('Saturday')) {
    return today === 6;  // Saturday
  }

  return dayMap[dayName] === today;
};

const getLocationUrl = (urlOrLatLong) => {
  if (urlOrLatLong.startsWith('http')) {
    return urlOrLatLong;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(urlOrLatLong)}`;
};

const getUniqueShiftsForPeriod = (dayName, period) => {
  const shifts = props.allRepsData
      .map(rep => {
        const slot = rep[dayName]?.[period];
        if (slot?.Role) {
          return {
            ...slot,
            repInfo: {
              name: rep.Name,
              id: rep['User ID']
            }
          };
        }
        return null;
      })
      .filter(Boolean);

  const shiftMap = new Map();

  shifts.forEach(shift => {
    const key = `${shift.Role}~${shift.Location}~${shift.Start}~${shift.End}`;

    if (!shiftMap.has(key)) {
      shiftMap.set(key, {
        ...shift,
        id: generateShiftId(shift, dayName, period),
        dayName,
        period,
        reps: []
      });
    }

    shiftMap.get(key).reps.push(shift.repInfo);
  });

  return Array.from(shiftMap.values());
};

const extractSlotInfo = (slot, dayName, period) => {
  if (!slot) return {};

  return {
    Role: slot.Role || null,
    Location: slot.Location || null,
    'URL or Lat/Long': slot['URL or Lat/Long'] || null,
    Start: slot.Start || null,
    End: slot.End || null,
    id: generateShiftId(slot, dayName, period) // Add ID to regular shifts
  };
};

const weeklySchedule = computed(() => {
  let rd = props.repData;
  if (!props.repData && !showAllShifts.value) return {};
  if (!rd) {
    rd = props.allRepsData[0];
  }

  const schedule = {};
  const days = [
    'Friday (Arrivals)',
    'Saturday (Arrivals)',
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday (Departures)',
    'Saturday (Departures)'
  ];

  days.forEach(day => {
    if (rd[day]) {
      schedule[day] = {
        Meeting: extractSlotInfo(rd[day].Meeting, day, 'Meeting'),
        Morning: extractSlotInfo(rd[day].Morning, day, 'Morning'),
        Afternoon: extractSlotInfo(rd[day].Afternoon, day, 'Afternoon'),
        Evening: extractSlotInfo(rd[day].Evening, day, 'Evening'),
        Night: extractSlotInfo(rd[day].Night, day, 'Night')
      };
    }
  });

  return schedule;
});


const shouldShowShift = (slot) => {
  return slot.Role && slot.Start && slot.End;
};

const hasShift = (slot) => {
  return slot.Role && slot.Start && slot.End;
};

const shouldShowDay = (day) => {
  if (showAllShifts.value) {
    const dayName = Object.keys(weeklySchedule.value).find(key => weeklySchedule.value[key] === day);
    return props.allRepsData.some(rep =>
        Object.values(rep[dayName] || {}).some(slot => slot?.Role)
    );
  }
  return Object.values(day).some(slot => hasShift(slot));
};

const handleShiftClick = (shift) => {
  if (!shift.id) {
    console.error('Shift ID is missing:', shift);
    return;
  }
  router.push(`/shift/${shift.id}`);
};
</script>

<style scoped>
.schedule-header-row {
  background-color: rgba(var(--tblr-body-color-rgb), 0.04);
}

tr.schedule-row-hover:hover {
  background-color: rgba(var(--tblr-body-color-rgb), 0.06);
}
</style>